<template>
  <div class="login-content">
    <div v-if="!showSuccessInfo || !userInfo?.user_id" class="items-center">
      <ContentLeft class="content-left" />
      <!-- 国内登录交互 -->
      <div class="content-right relative justify-center flex-1 flex-y-center">
        <div class="operation-wrap">
          <div class="decorate"></div>
          <div
            v-if="!isGlobalVersion() && !getEnv('privatization')"
            class="login-mode-switcher"
            @click="onChangeLoginModeClick"
          >
            <div class="radian"></div>
            <a-tooltip
              :title="loginMode === 'pwd' ? '微信登录' : '手机号/账号登录'"
              placement="bottom"
            >
              <iconpark-icon v-if="loginMode === 'pwd'" name="scan-login" />
              <iconpark-icon v-else name="pwd-login"></iconpark-icon>
            </a-tooltip>
          </div>
          <WechatLogin
            v-if="loginMode === 'wechat'"
            @success="onLoginSuccess"
            @accountSelect="accountSelect = true"
          ></WechatLogin>
          <template v-else>
            <AccountLogin
              v-if="status === 'login'"
              @back="status = 'resetPwd'"
              @success="onLoginSuccess"
              @accountSelect="accountSelect = true"
            />
            <AccountResetPwd
              v-else-if="status === 'resetPwd'"
              @back="status = 'login'"
            />
          </template>
          <SelectAccount v-model:open="accountSelect" @select="selectAccount" />
        </div>
      </div>
    </div>
    <div v-else class="h-full w-full">
      <Welcome :data="showSuccessInfo" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import Welcome from './components/welcome.vue'
import ContentLeft from './ContentLeft.vue'
import { showUpdateLog } from '~/hooks/useModal/modals/lastUpdate'
import SelectAccount from '~/features/account/select/selectModal.vue'
import {
  getUserAward,
  getProductUpdateMsg,
  getPermissionData,
  loginForWeb,
} from '~/api'
import AccountLogin from '~/features/account/login/base'
import WechatLogin from '~/features/account/login/wechatLogin.vue'
import AccountResetPwd from '~/features/account/reset/base.vue'
import { loginTask } from '~/utils/loginTask.ts'
import { redirectTo } from '~/utils'
import { emitter, EVENT } from '~/utils/eventbus'

type Status = 'login' | 'resetPwd'
const status = ref<Status>('login')
const showSuccessInfo = ref() // 注册成功页面
const userInfo = useUserInfo()
const session = useSession()
const accountSelect = ref(false)
const route = useRoute()

// 登录方式
type LoginMode = 'pwd' | 'wechat'

const loginMode = ref<LoginMode>(
  isChinese() && !getEnv('privatization') ? 'wechat' : 'pwd'
)

function onChangeLoginModeClick() {
  loginMode.value = loginMode.value === 'pwd' ? 'wechat' : 'pwd'
}

onMounted(() => {
  emitter.on(EVENT.SWITCH_WECHAT_LOGIN, () => (loginMode.value = 'wechat'))
  emitter.on(EVENT.FIND_PWD, () => (status.value = 'resetPwd'))
})

const redirect: any = route.query?.redirect

const isPpt = computed(() => redirect?.includes('/ppt-world'))
const isShanDong = computed(() => redirect?.includes('/shandong-innovation'))
const isHelpCenter = computed(() => redirect?.includes('/help-center'))

onBeforeMount(() => {
  if (import.meta.client) {
    const urlObj = new URL(window.location.href)
    const createTempWorkId = urlObj.searchParams.get('create_temp_work')
    // 编辑器的登录跳转的处理逻辑
    if (createTempWorkId) {
      loginTask.add({
        type: IAfterLoginActionType.USE_TEMPLATE,
        payload: [{ id: createTempWorkId }, TEMPLATE_SOURCE.EDITOR_CREATE],
      })
    }
  }
})

onBeforeUnmount(() => {
  loginTask.clear()
})

watch(
  () => userInfo.value?.user_id,
  (v) => {
    // 退出登录时
    if (!v) {
      showSuccessInfo.value = undefined
    }
  }
)

const permissionData = usePermissionData()

const onLoginSuccess = async () => {
  const [{ data }, { data: pData }] = await concurrency([
    getUserAward(),
    getPermissionData(),
  ])

  permissionData.value = pData
  // PPT 登录的后续结果
  if (
    !isPpt.value &&
    !isShanDong.value &&
    !isHelpCenter.value &&
    (data?.attraction_register_reward ||
      data?.personal_experience_register_info)
  ) {
    showSuccessInfo.value = data

    // 修改路由来触发 GA 的收集
    history.pushState('', '', '/login/thanks' + location.search)
    return
  }

  // 服务平台内记录的登录前任务列表
  if (loginTask.tasks.length > 0) {
    await loginTask.flush()

    return
  } else {
    const { data: productMsg } = await getProductUpdateMsg()
    // 登录后有更新通知
    if (productMsg?.tab_card_list) {
      showUpdateLog(productMsg?.tab_card_list, productMsg?.document_url)
    }
  }

  // 走自定义跳转
  if (redirect) {
    redirectTo(redirect)
    return
  }

  goHome()
}

const selectAccount = () => {
  onLoginSuccess()
  accountSelect.value = false
}

const decorateBg = computed(
  () =>
    `url(${getFeUrl('/youyan-platform/png/login/login-decorate-1.png?x-process=format,webp')})`
)
</script>

<style lang="scss" scoped>
.login-content {
  @apply w-full grow justify-center flex;
  background: var(--login-bg);
  min-height: calc(100vh - 60px);

  > div {
    @apply flex-1 flex w-full min-h-[640px] max-w-[1586px];
  }

  .content-right {
    .operation-wrap {
      overflow: hidden;
      position: relative;
      padding: 70px 90px 40px;
      width: 600px;
      min-height: 650px;
      background: var(--login-operation-bg);
      border-radius: 20px;
      border: 1px solid var(--back-3);
      .decorate {
        pointer-events: none;
        position: absolute;
        background-image: v-bind('decorateBg');
        background-size: 100% 100%;
        height: 192px;
        width: 192px;
        opacity: 0.3;
        top: 330px;
        right: 50px;
        filter: blur(20px);
      }
    }
  }
}

.login-mode-switcher {
  position: absolute;
  right: 0;
  top: 0;
  width: 79px;
  height: 76px;
  cursor: pointer;

  &::after,
  &::before {
    position: absolute;
    content: ' ';
    width: 0;
    height: 0;
    right: 0;
    bottom: 0;
  }
  &::after {
    // 左下角白色三角形
    border-top: 76px solid transparent;
    border-right: 0 solid transparent;
    border-left: 79px solid var(--login-operation-bg);
    pointer-events: none;
  }
  &::before {
    // 右上角蓝色三角形
    border-bottom: 76px solid transparent;
    border-left: 0 solid transparent;
    border-right: 79px solid var(--purple-1);
  }
  iconpark-icon {
    position: absolute;
    font-size: 27px;
    color: var(--primary-color);
    right: 15px;
    top: 15px;
  }
}
</style>
